import { useEffect, useRef, type ReactNode } from 'react';
import { Button } from '../../atoms/Button/Button';
import { Heading } from '../../atoms/Typography/Heading';
import { Text } from '../../atoms/Typography/Text';
import './ConfirmDialog.css';

export interface ConfirmDialogProps {
  readonly isOpen: boolean;
  readonly title: string;
  readonly message: string;
  readonly confirmLabel: string;
  readonly cancelLabel: string;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  /** Extra content between the message and the actions (e.g. a details list or a re-authentication field). */
  readonly children?: ReactNode;
  /** Disables the confirm button (e.g. until a required field is filled in). */
  readonly confirmDisabled?: boolean;
  /** Shows the confirm button as busy and disables both buttons while the action runs. */
  readonly isConfirming?: boolean;
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  children,
  confirmDisabled = false,
  isConfirming = false,
}: ConfirmDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedElement = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    previouslyFocusedElement.current = document.activeElement as HTMLElement | null;
    cancelButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
        return;
      }

      if (event.key === 'Tab') {
        const dialogNode = dialogRef.current;
        if (!dialogNode) {
          return;
        }

        const focusableElements = Array.from(
          dialogNode.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
        );

        if (focusableElements.length === 0) {
          return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (!firstElement || !lastElement) {
          return;
        }

        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocusedElement.current?.focus();
    };
  }, [isOpen, onCancel]);

  if (!isOpen) {
    return null;
  }

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (event.target === event.currentTarget) {
      onCancel();
    }
  };

  return (
    <div className="confirm-dialog-overlay" role="presentation" onMouseDown={handleOverlayClick}>
      <div
        ref={dialogRef}
        className="confirm-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-message"
      >
        <Heading level={2} id="confirm-dialog-title" className="confirm-dialog-title">
          {title}
        </Heading>
        <Text id="confirm-dialog-message" className="confirm-dialog-message">
          {message}
        </Text>
        {children != null && <div className="confirm-dialog-body">{children}</div>}
        <div className="confirm-dialog-actions">
          <Button
            ref={cancelButtonRef}
            variant="secondary"
            className="confirm-dialog-button confirm-dialog-button-secondary"
            onClick={onCancel}
            disabled={isConfirming}
          >
            {cancelLabel}
          </Button>
          <Button
            variant="danger"
            className="confirm-dialog-button confirm-dialog-button-danger"
            onClick={onConfirm}
            disabled={confirmDisabled || isConfirming}
            isLoading={isConfirming}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
